import React, {cloneElement, isValidElement, useMemo} from 'react'
import {Box, Flex, Text, useMultiStyleConfig} from '@chakra-ui/react'
import {useIntl} from 'react-intl'
import PropTypes from 'prop-types'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'

import NotifyMeButton from '../../../../components/notify-me-button'
import {useDerivedProduct} from '../../../../hooks/use-derived-product'
import DisplayPrice from '../../../../components/display-price'
import OmnibusPrice from '../../../../components/display-price/omnibus-price'
import {isProductPurchasable} from '../../../../utils/product-utils'
import {getOrderAvailabilityStatus} from '../../../../utils/order-availability-utils'
import {isBundleSet} from '../../../../utils/bundle-utils'

const PRODUCT_SUBTITLE_FIELDS = [
    'c_productSubtitle',
    'c_productSubTitle',
    'c_subtitle',
    'c_subTitle',
    'subtitle',
]

const StickyMenu = ({
    product = {},
    renderActionButtons = () => [],
    isAddToCartLoading = false,
    isSelectionPending = false,
    isHidden,
    orderReviewPriceData: priceData,
}) => {
    const {formatMessage} = useIntl()
    const {currency: activeCurrency} = useCurrency()
    const styles = useMultiStyleConfig('StickyMenu')

    const {variationAttributes} = useDerivedProduct(product, false, false)
    const color = variationAttributes?.find((el) => el.id === 'color')?.selectedValue.name

    const resolvedProductSubtitle = useMemo(
        () => PRODUCT_SUBTITLE_FIELDS.map((field) => product?.[field]).find(Boolean) || null,
        [product],
    )

    const resolvedMarketingColor = useMemo(() => {
        const marketingColor = product?.c_marketingColour
        if (
            typeof marketingColor === 'string' &&
            marketingColor.trim() &&
            !/^#/i.test(marketingColor)
        ) {
            return marketingColor
        }
        return color
    }, [product?.c_marketingColour, color])

    const productLine = useMemo(() => {
        const productName =
            product?.c_variantIdentifier || product?.name || product?.productName || ''
        const subtitleParts = [
            resolvedProductSubtitle,
            resolvedMarketingColor,
            product?.c_subModelIdentifier || product?.id,
        ].filter(Boolean)

        return {
            productName,
            subtitle: subtitleParts.join(', '),
        }
    }, [
        product?.c_variantIdentifier,
        product?.name,
        product?.productName,
        resolvedProductSubtitle,
        resolvedMarketingColor,
        product?.c_subModelIdentifier,
        product?.id,
    ])

    const actionButtons = renderActionButtons()
    const addToCartButton = actionButtons.find((button) => button.key === 'cart-button')
    const whereToBuyModal = actionButtons.find((button) => button.key === 'where-to-buy-modal')
    const orderStatus = getOrderAvailabilityStatus(product)
    const notifyMe = !isBundleSet(product) && product?.inventory?.stockLevel === 0
    const productSummaryText = productLine.subtitle
        ? `${productLine.productName}, ${productLine.subtitle}`
        : productLine.productName

    const accessibleAddToCartButton = useMemo(() => {
        if (!isValidElement(addToCartButton)) {
            return addToCartButton
        }

        return cloneElement(addToCartButton, {
            isLoading: isAddToCartLoading,
            isDisabled: isSelectionPending,
            tabIndex: isHidden ? -1 : addToCartButton.props?.tabIndex,
            'aria-hidden': isHidden ? true : addToCartButton.props?.['aria-hidden'],
            variant: 'primary-not-rounded',
            size: 'large',
            height: '36px',
            maxWidth: {base: '100%', lg: 'fit-content'},
        })
    }, [addToCartButton, isHidden])

    return (
        <Box
            data-testid="sticky-menu"
            {...styles.stickyBar}
            role="region"
            aria-label={formatMessage({
                id: 'sticky_menu.aria.label',
                defaultMessage: 'Sticky product actions',
            })}
        >
            <Flex layerStyle="full-width" {...styles.stickyBarContent}>
                <Flex {...styles.productContainer}>
                    <Text
                        {...styles.productText}
                        title={productSummaryText}
                        aria-label={productSummaryText}
                    >
                        {productLine.productName}
                        {productLine.subtitle && (
                            <Text as="span" {...styles.productSubtitleText}>
                                {`, ${productLine.subtitle}`}
                            </Text>
                        )}
                    </Text>
                </Flex>
                <Flex {...styles.actionsContainer}>
                    <Flex {...styles.priceContainer}>
                        <Flex {...styles.priceMainRow}>
                            {priceData?.currentPrice ? (
                                <DisplayPrice
                                    priceData={priceData}
                                    currency={product?.currency || activeCurrency}
                                    productDetailPriceView={true}
                                    productDetailPriceAs="p"
                                    priceContainerProps={{
                                        gap: [
                                            'xxxsmall.base',
                                            'xxxsmall.sm',
                                            'xxxsmall.lg',
                                            'xxxsmall.xl',
                                        ],
                                    }}
                                    showDiscount={false}
                                    variant="sticky"
                                    showPriceInfoPopover={true}
                                    currentPriceProps={{fontWeight: 'medium'}}
                                />
                            ) : null}
                        </Flex>

                        {priceData?.showOmnibusPrice && (
                            <Flex {...styles.omnibusContainer}>
                                <OmnibusPrice
                                    priceData={priceData}
                                    currency={product?.currency || activeCurrency}
                                    omnibusExpandedView={false}
                                    showDiscount={false}
                                    variant="sticky"
                                />
                            </Flex>
                        )}
                    </Flex>

                    <Box {...styles.buttonContainer}>
                        {isProductPurchasable(product) && notifyMe && !orderStatus.isPreorder ? (
                            <NotifyMeButton
                                product={product}
                                tabIndex={isHidden ? -1 : undefined}
                                variant="outline-not-rounded"
                            />
                        ) : (
                            <>
                                {accessibleAddToCartButton}
                                {whereToBuyModal}
                            </>
                        )}
                    </Box>
                </Flex>
            </Flex>
        </Box>
    )
}

StickyMenu.propTypes = {
    product: PropTypes.object,
    renderActionButtons: PropTypes.func,
    isAddToCartLoading: PropTypes.bool,
    isSelectionPending: PropTypes.bool,
    isHidden: PropTypes.bool,
    orderReviewPriceData: PropTypes.object,
}

export default StickyMenu
